import React, { useEffect } from "react";
import { ISelfHelpContext, IrisWebChat } from "@iris/react-webchat";
import "@iris/react-webchat/dist/index.css";
import { ServiceContext } from "@msx/platform-services";
import { getConsumptionAPI } from "../../utils/httpUtils";
import { InjectedIntlProps, injectIntl } from "react-intl";
import "./css/chatbot.css";
import { IRISBotSettings } from "./IrisSelfHelpContext";
import { SETBotToken,setIsBotError, setIsLoader } from "../../../core/store";
import { useDispatch } from "react-redux";

export interface ExtendedChatBotProps {
  irisSelfHelp: ISelfHelpContext;
  appName: string;
  platform: string;
}

export interface ChatbotProp extends InjectedIntlProps, ExtendedChatBotProps {
  //todo: if any
  showBot: boolean;
  irisApiBaseUrl: string;
  closeBotHandler: any;
  irisToken: string;
  irisTokenCreated:any;
  setIrisTokenCreated:any;
}

const IrisChatBot: React.FC<ChatbotProp> = (props) => {

  const reduxDispatch = useDispatch();

  const context = React.useContext(ServiceContext);


  useEffect(() => {
    const getIrisTokenGenerator = async () => {
      try {
        const response = await getConsumptionAPI(
          "/v1/mcapshelp/bot/GetToken",
          context.authClient
        );

        reduxDispatch(setIsBotError(false));

        if (response.status !== 200) {
          reduxDispatch(SETBotToken(null));
          reduxDispatch(setIsLoader(true));
          reduxDispatch(setIsBotError(true));
        } else if (response.data) {
          props.setIrisTokenCreated(response.data.token); 
          reduxDispatch(SETBotToken(response.data.token));   
        }
      } catch (error) {
        console.error("Error generating token:", error);
      }
    };

    if (!props.irisTokenCreated) {
      getIrisTokenGenerator();
    }
  }, [props.irisTokenCreated]);

  // Accessibility fix: Add proper tabindex and aria-label to webchat send button for focus-order compliance
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    let observer: MutationObserver | null = null;

    const fixWebChatAccessibility = () => {
      const sendButtons = document.querySelectorAll(
        '.webchat__send-button, .webchat__send-box__button, button.webchat__icon-button'
      );
      sendButtons.forEach((button) => {
        if (!button.getAttribute('aria-label')) {
          button.setAttribute('aria-label', 'Send message');
        }
        if (!button.hasAttribute('tabindex') || button.getAttribute('tabindex') === '-1') {
          button.setAttribute('tabindex', '0');
        }
      });

      // Upload/attachment button: ensure keyboard focus-order compliance without
      // mislabeling it as "Send message" (WCAG 2.1.1 Keyboard).
      const uploadButtons = document.querySelectorAll(
        '.webchat__upload-button, button[title="Upload file"]'
      );
      uploadButtons.forEach((button) => {
        if (!button.getAttribute('aria-label')) {
          button.setAttribute('aria-label', 'Upload file');
        }
        if (!button.hasAttribute('tabindex') || button.getAttribute('tabindex') === '-1') {
          button.setAttribute('tabindex', '0');
        }
      });
    };

    // Run fix after component mounts and on DOM changes
    if (props.showBot && props.irisTokenCreated) {
      // Initial fix with delay to allow webchat to render
      timeoutId = setTimeout(fixWebChatAccessibility, 500);
      
      // MutationObserver to handle dynamic content
      observer = new MutationObserver(() => {
        fixWebChatAccessibility();
      });

      const botContainer = document.querySelector('.bot-container');
      if (botContainer) {
        observer.observe(botContainer, { childList: true, subtree: true });
      }
    }

    // Always return cleanup function
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (observer) {
        observer.disconnect();
      }
    };
  }, [props.showBot, props.irisTokenCreated]); 

  const closeBot = () => {
    
    props.closeBotHandler();
  };

  const IrisBot = React.useMemo(
    () =>
      React.createElement(IrisWebChat, {
        acquireToken: () => Promise.resolve(props.irisTokenCreated),
        closeBot,
        enableAutoSuggest: true,
        selfHelpContext: props.irisSelfHelp,
        irisApiBaseUrl: props.irisApiBaseUrl || "https://iris-api-dev.azurewebsites.net",
        appName: props.appName,
        platform: props.platform,
        hideCloseButton: true,
        style: {
          avatarIconUrl: IRISBotSettings.CHATBOT_AVATAR_ICON_URL,
          // Accessibility (WCAG 1.4.3): white text on #0078d4 is only 4.53:1
          // (borderline). Darkened to #106ebe (5.3:1) for a safe AA margin on
          // the user message bubbles and header. Visual-only change.
          userActivityBackgroundColor: "#106ebe",
          headerBackgroundColor: "#106ebe",
          chatWindowBacgroundColor: "#dfe3ee",
        },
      }),
    [props.irisTokenCreated, props]
  );

  return props.showBot && props.irisTokenCreated ? (
    <div className="bot-container">
      <div>{IrisBot}</div>
    </div>
  ) : null;
};

export const ChatBot = injectIntl(IrisChatBot);

// Generated by Copilot
// updated on: 2026-07-24 | 3 lines changed | [Auto Generated] | A11y WCAG 1.4.3: darkened user bubble/header blue #0078d4->#106ebe (4.53->5.26:1) for AA contrast margin | Time Taken 2 min
// Generated by Copilot
// updated on: 2026-07-28 | 14 lines added | [Auto Generated] | A11y WCAG 2.1.1: extend fixWebChatAccessibility to force tabindex=0 + aria-label 'Upload file' on webchat upload button for keyboard focus-order | Time Taken 2 min
